import { useMemo, useState } from 'react';
import {
  Button,
  Input,
  Label,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogBody,
  DialogFooter,
} from '@vnetwork/ui/primitives';
import { useToast } from '@vnetwork/ui/feedback';
import {
  useCreateVolume,
  useAttachVolume,
  useDetachVolume,
  useResizeDisk,
  useCreateSnapshot,
  resolveVolumeDiskId,
  useInstanceList,
  useInstanceDetail,
  VOLUME_TYPES,
  type Volume,
} from '@/api/opennebula';

const toGB = (mb?: number) => (mb ? Math.round((Number(mb) / 1024) * 10) / 10 : 0);

// ---- Create --------------------------------------------------------------

export function VolumeCreateDialog({
  open,
  onOpenChange,
  serviceId,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  serviceId: string;
}) {
  const { show } = useToast();
  const [name, setName] = useState('');
  const [size, setSize] = useState('5');
  const [type, setType] = useState<Volume['type']>('hiops');

  const create = useCreateVolume(serviceId, {
    onSuccess: () => {
      show({ tone: 'success', title: 'Volume created' });
      onOpenChange(false);
      setName('');
      setSize('5');
      setType('hiops');
    },
    onError: () => show({ tone: 'danger', title: 'Could not create volume' }),
  });

  const sizeNum = Number(size);
  const valid = name.trim() && sizeNum > 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Create volume</DialogTitle>
        </DialogHeader>
        <DialogBody>
          <div style={{ display: 'grid', gap: 'var(--vn-space-3)' }}>
            <div>
              <Label>Volume name</Label>
              <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="data-volume" />
            </div>
            <div>
              <Label>Size (GiB)</Label>
              <Input type="number" min={1} value={size} onChange={(e) => setSize(e.target.value)} />
            </div>
            <div>
              <Label>Volume type</Label>
              <Select value={type} onValueChange={(v) => setType(v as Volume['type'])}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {VOLUME_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
        </DialogBody>
        <DialogFooter>
          <Button variant="secondary" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button
            disabled={!valid || create.isPending}
            onClick={() => create.mutate({ name: name.trim(), size: sizeNum, type })}
          >
            Create
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---- Attach (detached volume → instance) ---------------------------------

export function VolumeAttachDialog({
  serviceId,
  volume,
  onClose,
}: {
  serviceId: string;
  volume: Volume | null;
  onClose: () => void;
}) {
  const { show } = useToast();
  const { data: instances = [] } = useInstanceList(serviceId);
  const [instanceId, setInstanceId] = useState('');

  const attach = useAttachVolume(serviceId, {
    onSuccess: () => {
      show({ tone: 'success', title: 'Volume attached' });
      onClose();
      setInstanceId('');
    },
    onError: () => show({ tone: 'danger', title: 'Attach failed' }),
  });

  return (
    <Dialog open={!!volume} onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Attach volume</DialogTitle>
        </DialogHeader>
        <DialogBody>
          <p style={{ marginTop: 0, color: 'var(--vn-fg-muted)' }}>
            Attach <strong>{volume?.name}</strong> ({toGB(volume?.size)} GB) to an instance.
          </p>
          <Label>Instance</Label>
          <Select value={instanceId} onValueChange={setInstanceId}>
            <SelectTrigger><SelectValue placeholder="Choose an instance" /></SelectTrigger>
            <SelectContent>
              {instances.map((i) => <SelectItem key={i.id} value={i.id}>{i.name} ({i.id})</SelectItem>)}
            </SelectContent>
          </Select>
          {instances.length === 0 && (
            <p style={{ marginBottom: 0, fontSize: 'var(--vn-font-size-sm)', color: 'var(--vn-fg-muted)' }}>
              No instances available for attachment.
            </p>
          )}
        </DialogBody>
        <DialogFooter>
          <Button variant="secondary" onClick={onClose}>Cancel</Button>
          <Button
            disabled={!instanceId || attach.isPending}
            onClick={() => volume?.id && attach.mutate({ instanceId, volumeId: volume.id })}
          >
            Attach volume
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---- Resize / Snapshot / Detach (attached volume) ------------------------
// These act through the owning instance, so we fetch its detail to map the
// volume to a disk index (`disk_id`).

function useResolvedDisk(serviceId: string, volume: Volume | null) {
  const instanceId = volume?.attached?.instance_id;
  const { data: instance } = useInstanceDetail(serviceId, instanceId);
  return useMemo(
    () => resolveVolumeDiskId(instance?.metadata?.disks, volume?.id),
    [instance, volume?.id],
  );
}

export function VolumeResizeDialog({
  serviceId,
  volume,
  onClose,
}: {
  serviceId: string;
  volume: Volume | null;
  onClose: () => void;
}) {
  const { show } = useToast();
  const instanceId = volume?.attached?.instance_id ?? '';
  const diskId = useResolvedDisk(serviceId, volume);
  const current = toGB(volume?.size);
  const [size, setSize] = useState('');

  const resize = useResizeDisk(serviceId, instanceId, {
    onSuccess: () => {
      show({ tone: 'success', title: 'Resize requested' });
      onClose();
      setSize('');
    },
    onError: () => show({ tone: 'danger', title: 'Resize failed' }),
  });

  const next = Number(size);
  const valid = next > current && diskId != null;

  return (
    <Dialog open={!!volume} onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Resize {volume?.name}</DialogTitle>
        </DialogHeader>
        <DialogBody>
          <Label>New size (GB) — current {current} GB</Label>
          <Input type="number" min={current + 1} value={size} onChange={(e) => setSize(e.target.value)} placeholder={`> ${current}`} />
          <p style={{ margin: 'var(--vn-space-1) 0 0', fontSize: 'var(--vn-font-size-sm)', color: 'var(--vn-fg-muted)' }}>
            Volumes can only be grown, not shrunk (max 1000 GB).
          </p>
        </DialogBody>
        <DialogFooter>
          <Button variant="secondary" onClick={onClose}>Cancel</Button>
          <Button
            disabled={!valid || next > 1000 || resize.isPending}
            onClick={() => diskId && resize.mutate({ disk_id: diskId, size: next })}
          >
            Resize volume
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function VolumeSnapshotDialog({
  serviceId,
  volume,
  onClose,
}: {
  serviceId: string;
  volume: Volume | null;
  onClose: () => void;
}) {
  const { show } = useToast();
  const instanceId = volume?.attached?.instance_id ?? '';
  const diskId = useResolvedDisk(serviceId, volume);
  const [name, setName] = useState('');

  const snap = useCreateSnapshot(serviceId, instanceId, {
    onSuccess: () => {
      show({ tone: 'success', title: 'Snapshot created' });
      onClose();
      setName('');
    },
    onError: () => show({ tone: 'danger', title: 'Snapshot failed' }),
  });

  const defaultName = volume ? `${volume.name}-snapshot` : '';

  return (
    <Dialog open={!!volume} onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Create snapshot</DialogTitle>
        </DialogHeader>
        <DialogBody>
          <p style={{ marginTop: 0, color: 'var(--vn-fg-muted)' }}>
            Capture the current state of <strong>{volume?.name}</strong>.
          </p>
          <Label>Snapshot name</Label>
          <Input value={name} onChange={(e) => setName(e.target.value)} placeholder={defaultName} />
        </DialogBody>
        <DialogFooter>
          <Button variant="secondary" onClick={onClose}>Cancel</Button>
          <Button
            disabled={diskId == null || snap.isPending}
            onClick={() => diskId && snap.mutate({ name: (name.trim() || defaultName), disk_id: diskId })}
          >
            Create snapshot
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function VolumeDetachDialog({
  serviceId,
  volume,
  onClose,
}: {
  serviceId: string;
  volume: Volume | null;
  onClose: () => void;
}) {
  const { show } = useToast();
  const instanceId = volume?.attached?.instance_id ?? '';
  const diskId = useResolvedDisk(serviceId, volume);

  const detach = useDetachVolume(serviceId, {
    onSuccess: () => {
      show({ tone: 'success', title: 'Volume detached' });
      onClose();
    },
    onError: () => show({ tone: 'danger', title: 'Detach failed' }),
  });

  return (
    <Dialog open={!!volume} onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Detach volume?</DialogTitle>
        </DialogHeader>
        <DialogBody>
          <p style={{ margin: 0 }}>
            Detach <strong>{volume?.name}</strong> from <strong>{volume?.attached?.name}</strong>? Data on the
            volume is preserved and it can be re-attached later.
          </p>
        </DialogBody>
        <DialogFooter>
          <Button variant="secondary" onClick={onClose}>Cancel</Button>
          <Button
            variant="danger"
            disabled={diskId == null || detach.isPending}
            onClick={() => volume?.id && diskId && detach.mutate({ instanceId, volumeId: volume.id, diskId })}
          >
            Detach
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
